import { useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Send, MapPin, Mail, Phone, Paperclip, X, FileText } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { SITE } from "@/lib/siteData";
import { trackLeadSubmission, getGaClientId } from "@/lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROJECT_TYPES = ["Brand Film", "Commercial", "Corporate", "Documentary", "Photography", "Other"];
const MAX_FILES = 3;
const MAX_TOTAL_BYTES = 10 * 1024 * 1024;
const ACCEPT = ".pdf,.doc,.docx,.txt,.rtf,.odt,.ppt,.pptx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.webp,application/pdf";

const formatSize = (n) => n < 1024 * 1024 ? `${Math.round(n / 1024)} KB` : `${(n / (1024 * 1024)).toFixed(1)} MB`;

export const ContactCTA = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    project_type: "",
    budget: "",
    brief: "",
  });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setType = (t) => setForm({ ...form, project_type: t });

  const addFiles = (incoming) => {
    const next = [...files];
    let total = files.reduce((s, f) => s + f.size, 0);
    for (const f of incoming) {
      if (next.length >= MAX_FILES) { toast.error(`You can attach up to ${MAX_FILES} files.`); break; }
      if (total + f.size > MAX_TOTAL_BYTES) { toast.error("Combined file size exceeds 10 MB."); break; }
      if (next.find((x) => x.name === f.name && x.size === f.size)) continue;
      next.push(f);
      total += f.size;
    }
    setFiles(next);
  };

  const onFilesChange = (e) => {
    if (e.target.files?.length) addFiles(Array.from(e.target.files));
    e.target.value = "";
  };

  const removeFile = (idx) => setFiles(files.filter((_, i) => i !== idx));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please fill in your name and email.");
      return;
    }
    if (!form.brief.trim() && files.length === 0) {
      toast.error("Please add a short brief or attach a file.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
      files.forEach((f) => fd.append("files", f));
      const gaCid = getGaClientId();
      if (gaCid) fd.append("ga_client_id", gaCid);
      await axios.post(`${API}/contact`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      trackLeadSubmission({ source: "contact-cta", formName: "Homepage / Contact page brief form", value: 500 });
      toast.success("Thanks - your brief landed safely. We'll be in touch shortly.");
      setForm({ name: "", email: "", company: "", project_type: "", budget: "", brief: "" });
      setFiles([]);
      // Redirect to the confirmation page so ad networks can build retargeting
      // audiences and the drip trigger fires from a clean, dedicated URL.
      navigate("/thank-you?source=contact-cta");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-cta-section" className="section-pad relative bg-background overflow-hidden">
      <div className="absolute inset-0 -z-10 opacity-60" aria-hidden>
        <div className="absolute -top-32 -right-32 h-[480px] w-[480px] rounded-full blur-3xl bg-[hsl(var(--primary)/0.12)]" />
        <div className="absolute bottom-0 -left-32 h-[420px] w-[420px] rounded-full blur-3xl bg-[hsl(225,40%,30%)]/30" />
      </div>

      <div className="container-site">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }} className="lg:col-span-5">
            <p className="kicker mb-3">Let&apos;s make something</p>
            <h2 className="font-display uppercase text-foreground text-[clamp(2.25rem,5.5vw,4.5rem)] leading-[0.95] text-balance">
              Tell us about<br /><span className="text-[hsl(var(--primary))]">your project.</span>
            </h2>
            <p className="mt-6 text-sm sm:text-base text-muted-foreground leading-relaxed max-w-md">
              A short brief is enough to start. We&apos;ll come back with a creative response, a treatment direction, and a clear estimate.
            </p>
            <p className="mt-4 text-xs text-muted-foreground max-w-md">
              Already have a brief, deck or RFP? Drop it in - PDF, Word, slides or images all work.
            </p>

            <ul className="mt-10 space-y-4 text-sm">
              <li className="flex items-start gap-3"><MapPin className="h-4 w-4 text-[hsl(var(--primary))] mt-0.5" /><span className="text-muted-foreground">{SITE.location}</span></li>
              <li className="flex items-start gap-3"><Mail className="h-4 w-4 text-[hsl(var(--primary))] mt-0.5" /><a href={`mailto:${SITE.email}`} className="text-foreground hover:text-[hsl(var(--primary))] transition-colors">{SITE.email}</a></li>
              <li className="flex items-start gap-3"><Phone className="h-4 w-4 text-[hsl(var(--primary))] mt-0.5" /><a href={`tel:${SITE.phone.replace(/\s+/g, "")}`} className="text-foreground hover:text-[hsl(var(--primary))] transition-colors">{SITE.phone}</a></li>
            </ul>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }} className="lg:col-span-7">
            <form data-testid="contact-form" onSubmit={onSubmit} className="rounded-2xl border border-border bg-card/80 backdrop-blur-sm p-6 sm:p-8 shadow-[0_30px_80px_rgba(0,0,0,0.4)]" noValidate encType="multipart/form-data">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Name</Label>
                  <Input id="name" data-testid="contact-name-input" value={form.name} onChange={update("name")} placeholder="Your full name" className="bg-background border-border h-11" autoComplete="name" required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Email</Label>
                  <Input id="email" data-testid="contact-email-input" type="email" value={form.email} onChange={update("email")} placeholder="you@brand.com" className="bg-background border-border h-11" autoComplete="email" required />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="company" className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Company <span className="text-muted-foreground/60 normal-case tracking-normal">(optional)</span></Label>
                  <Input id="company" data-testid="contact-company-input" value={form.company} onChange={update("company")} placeholder="Brand or agency" className="bg-background border-border h-11" />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Project type</Label>
                  <div data-testid="contact-project-type-chips" className="flex flex-wrap gap-2">
                    {PROJECT_TYPES.map((t) => {
                      const active = form.project_type === t;
                      return (
                        <button type="button" key={t} data-testid={`contact-project-type-${t.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setType(active ? "" : t)} className={`h-9 px-4 rounded-full text-xs uppercase tracking-[0.18em] border transition-colors ${active ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-transparent" : "bg-background border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"}`}>{t}</button>
                      );
                    })}
                  </div>
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="budget" className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Indicative budget <span className="text-muted-foreground/60 normal-case tracking-normal">(optional)</span></Label>
                  <Input id="budget" data-testid="contact-budget-input" value={form.budget} onChange={update("budget")} placeholder="e.g. R150k - R500k" className="bg-background border-border h-11" />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="brief" className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Project brief <span className="text-muted-foreground/60 normal-case tracking-normal">(optional if attaching files)</span></Label>
                  <Textarea id="brief" data-testid="contact-brief-textarea" value={form.brief} onChange={update("brief")} placeholder="A short paragraph: what you're making, who it's for, when you need it. Or skip and attach a brief document below." className="bg-background border-border min-h-[120px]" />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Attach brief documents <span className="text-muted-foreground/60 normal-case tracking-normal">(optional, up to 3 files / 10 MB)</span></Label>
                  <div className="rounded-xl border border-dashed border-border bg-background p-4">
                    <input ref={fileInputRef} type="file" multiple accept={ACCEPT} className="hidden" onChange={onFilesChange} data-testid="contact-file-input" />
                    <div className="flex flex-wrap items-center gap-3">
                      <Button type="button" data-testid="contact-attach-button" onClick={() => fileInputRef.current?.click()} variant="ghost" className="h-10 rounded-full border border-border bg-card hover:bg-[hsl(225,18%,9%)] px-4">
                        <Paperclip className="mr-2 h-4 w-4" />Add file(s)
                      </Button>
                      <span className="text-xs text-muted-foreground">PDF, Word, slides, images. Each file under 4 MB.</span>
                    </div>
                    {files.length > 0 && (
                      <ul data-testid="contact-files-list" className="mt-4 space-y-2">
                        {files.map((f, i) => (
                          <li key={`${f.name}-${i}`} className="flex items-center justify-between gap-3 rounded-lg bg-card/60 border border-border px-3 py-2">
                            <div className="flex items-center gap-3 min-w-0">
                              <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-[hsl(225,16%,14%)] border border-border text-[hsl(var(--primary))] shrink-0"><FileText className="h-4 w-4" /></span>
                              <div className="min-w-0">
                                <div className="text-sm text-foreground truncate">{f.name}</div>
                                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{formatSize(f.size)}</div>
                              </div>
                            </div>
                            <button type="button" data-testid={`contact-remove-file-${i}`} onClick={() => removeFile(i)} className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-card hover:bg-[hsl(225,18%,9%)] border border-border text-muted-foreground hover:text-foreground shrink-0" aria-label={`Remove ${f.name}`}>
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <p className="text-xs text-muted-foreground">We typically reply within one business day.</p>
                <Button type="submit" data-testid="contact-submit-button" disabled={submitting} className="h-12 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-110 px-6 text-sm font-medium disabled:opacity-60">
                  {submitting ? "Sending…" : (<>Send brief <Send className="ml-2 h-4 w-4" /></>)}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
